import { BodyText } from '@/components/BodyText';
import { Box, Divider } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Link, useMatch } from 'react-router-dom';
import { AssetsIcon } from '@/components/AssetsIcon';
import { CompareIcon } from '@/components/CompareIcon';
import { SpaceDashboard, TableChart, TravelExplore } from '@mui/icons-material';
import { PATHS } from '@/utils/paths';
import { useTenants } from '@/services/tenants/useTenants';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useCompareCart } from '@/services/compareCart';

const NavigationContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '75px',
  flexShrink: 0,
  transition: 'width 0.3s ease-in-out',
  position: 'relative',
}));

const NavLink = styled(Link, {
  shouldForwardProp: property => property !== 'selected',
})<{ selected?: boolean }>(({ selected, theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: selected ? '#ffffff' : theme.palette.text.secondary,
  textDecoration: 'none',
  backgroundColor: selected ? theme.palette.secondary.main : 'transparent',
  borderRadius: '6px',
  margin: '6px',
  padding: '7.5px 0',
  border: '1px solid transparent',
  '&:hover': {
    backgroundColor: selected ? theme.palette.secondary.main : theme.palette.secondary.hover,
  },
}));

const getIconColor = (isSelected: boolean, textSecondary: string) => (isSelected ? 'white' : textSecondary);

export const Navigation = () => {
  const theme = useTheme();
  const { ASSETS, REPORT, COMPARE, PART_FINDER, DASHBOARD } = PATHS;
  const { getPath } = useTenants();
  const { isPartFinderEnabled } = useFeatureFlags();
  const { currentTenant, currentUserIsAtleastTenantViewer } = useGetCurrentUser();
  const { selectedForCompare } = useCompareCart();

  const isAssetsPage = !!useMatch(ASSETS);
  const isReportPage = !!useMatch(REPORT);
  const isComparePage = !!useMatch(COMPARE);
  const isPartFinderPage = !!useMatch(PART_FINDER);
  const isDashboardPage = !!useMatch(DASHBOARD);

  const getComparePath = () => {
    const basePath = getPath('compare');
    if (selectedForCompare.asset) {
      const parameters = new URLSearchParams({
        asset1Id: selectedForCompare.asset.id,
        tenant1Id: selectedForCompare.asset.tenantId,
      });
      return `${basePath}?${parameters}`;
    }
    return basePath;
  };

  return (
    <NavigationContainer>
      {currentUserIsAtleastTenantViewer(currentTenant?.tenantId) && (
        <>
          <NavLink to={getPath('dashboard')} selected={isDashboardPage}>
            <SpaceDashboard
              sx={{
                color: getIconColor(isDashboardPage, theme.palette.text.secondary),
                fontSize: '24px',
              }}
            />
            <BodyText marginTop='3px' fontSize='0.55rem' lineHeight='0.84rem' textAlign='center'>
              DASHBOARD
            </BodyText>
          </NavLink>
          <Divider data-testid='dashboard-nav-divider' sx={{ margin: '6px 12px' }} />
        </>
      )}
      <NavLink to={getPath('assets')} selected={isAssetsPage}>
        <AssetsIcon
          sx={{
            color: getIconColor(isAssetsPage, theme.palette.text.secondary),
            fontSize: '24px',
          }}
        />
        <BodyText marginTop='3px' fontSize='0.6rem'>
          ASSETS
        </BodyText>
      </NavLink>
      <NavLink to={getComparePath()} selected={isComparePage}>
        <CompareIcon
          sx={{
            color: getIconColor(isComparePage, theme.palette.text.secondary),
            fontSize: '24px',
          }}
        />
        <BodyText marginTop='3px' fontSize='0.6rem'>
          COMPARE
        </BodyText>
      </NavLink>
      <NavLink to={getPath('report')} selected={isReportPage}>
        <TableChart
          sx={{
            color: getIconColor(isReportPage, theme.palette.text.secondary),
            fontSize: '24px',
          }}
        />
        <BodyText marginTop='3px' fontSize='0.6rem'>
          REPORTS
        </BodyText>
      </NavLink>
      {isPartFinderEnabled && (
        <NavLink to={getPath('part-finder')} selected={isPartFinderPage}>
          <TravelExplore
            sx={{
              color: getIconColor(isPartFinderPage, theme.palette.text.secondary),
              fontSize: '24px',
            }}
          />
          <BodyText marginTop='3px' fontSize='0.55rem' lineHeight='0.84rem' textAlign='center' whiteSpace='nowrap'>
            PART FINDER
          </BodyText>
        </NavLink>
      )}
    </NavigationContainer>
  );
};
